/**
 * Vite React Refresh preamble installer.
 *
 * When the Vite dev server is active, React components cannot be
 * hot-reloaded without the React Refresh runtime being injected
 * before any JSX module is imported. Vite's normal plugin does
 * this via an HTML-time transform that prepends a `<script
 * type="module">` block; Drupal does not run through Vite's HTML
 * pipeline, so we install the preamble ourselves from this
 * module and wait on the shared promise before importing any
 * component.
 *
 * Readiness is signaled two ways and both must complete:
 *
 * - The preamble script dispatches a custom event as its last
 *   statement, confirming React Refresh hooked into the global.
 * - The @vite/client script fires its native `load` event when
 *   it is parsed and executed.
 *
 * If either script fails to load, we resolve the promise anyway
 * with `devMode` flipped off so the mount engine falls back to
 * production-built component bundles. No magic timeouts, no
 * polling loops - both paths resolve deterministically.
 */

import { readReactSettings } from './types';

const PREAMBLE_READY_EVENT = 'sdx:vite-preamble-ready';

let viteClientReady: Promise<void> | null = null;

declare global {
  interface Window {
    __vite_plugin_react_preamble_installed__?: boolean;
  }
}

/**
 * Returns a shared promise that resolves once the Vite client
 * and React Refresh preamble are both loaded, or immediately
 * when no dev server is configured.
 */
export function ensureVitePreamble(devServer: string | null): Promise<void> {
  if (!devServer) {
    return Promise.resolve();
  }
  if (viteClientReady) {
    return viteClientReady;
  }
  viteClientReady = installPreamble(devServer);
  return viteClientReady;
}

function installPreamble(devServer: string): Promise<void> {
  const preambleReady = new Promise<void>((resolve) => {
    if (window.__vite_plugin_react_preamble_installed__) {
      resolve();
      return;
    }
    window.addEventListener(PREAMBLE_READY_EVENT, () => resolve(), { once: true });
  });

  const clientReady = new Promise<void>((resolve) => {
    const script = document.createElement('script');
    script.type = 'module';
    script.src = `${devServer}/@vite/client`;
    script.onload = () => resolve();
    script.onerror = () => {
      console.warn('[SDX React] Dev server unreachable, falling back to production builds.');
      // Clear the dev server so subsequent mounts go through the
      // production dist path instead of dead-retrying against an
      // unreachable Vite instance.
      const settings = readReactSettings();
      if (settings) {
        settings.dev_server = null;
      }
      resolve();
    };
    document.head.appendChild(script);
  });

  const preambleScript = document.createElement('script');
  preambleScript.type = 'module';
  preambleScript.setAttribute('data-sdx-vite-client', 'true');
  preambleScript.textContent = [
    `import RefreshRuntime from "${devServer}/@react-refresh";`,
    `RefreshRuntime.injectIntoGlobalHook(window);`,
    `window.$RefreshReg$ = () => {};`,
    `window.$RefreshSig$ = () => (type) => type;`,
    `window.__vite_plugin_react_preamble_installed__ = true;`,
    `window.dispatchEvent(new Event("${PREAMBLE_READY_EVENT}"));`,
  ].join('');
  document.head.appendChild(preambleScript);

  return Promise.all([preambleReady, clientReady]).then(() => undefined);
}
